// src/components/pages/simulation/hooks/useMonteCarlo.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import { runSimulationInWorkers, getDefaultDates } from './../utils/MonteCarloUtils'; 

export function useMonteCarlo(t) {
    const navigate = useNavigate();
    const location = useLocation();
    
    // --- 1. 초기 상태 로드 (URL 파싱) ---
    const getInitialState = () => {
        const params = new URLSearchParams(location.search);
        const { today, thirtyYearsAgo } = getDefaultDates();
        
        let initialRows = [{ ticker: "", weight: "" }];
        const urlTickers = params.get('tickers');
        const urlWeights = params.get('weights');
        
        if (urlTickers && urlWeights) {
            try {
                const tArr = urlTickers.split(',');
                const wArr = urlWeights.split(',');
                if (tArr.length === wArr.length) {
                    initialRows = tArr.map((tick, i) => ({
                        ticker: tick.toUpperCase(),
                        weight: (parseFloat(wArr[i]) * 100).toFixed(2)
                    }));
                }
            } catch(e) { console.error(e); }
        }

        const inv = params.get('investment') || "100000";
        const wRate = params.get('wRate') || "4";
        const wAmount = params.get('wAmount') || (Number(inv) * (Number(wRate) / 100)).toFixed(0);

        return {
            rows: initialRows,
            investment: inv,
            targetAmount: params.get('targetAmount') || "200000",
            simYears: Number(params.get('simYears')) || 20,
            simCount: Number(params.get('simCount')) || 5000, 
            currency: params.get('currency') || "USD",
            dataPeriod: Number(params.get('dataPeriod')) || 30,
            isCustomDate: !!(params.get('startDate') || params.get('endDate')),
            startDate: params.get('startDate') || thirtyYearsAgo,
            endDate: params.get('endDate') || today,
            
            monthlyContribution: params.get('monthly') || "0",
            contributionIncrease: params.get('increase') || "0",
            
            isWithdrawalMode: params.get('mode') === 'withdrawal',
            withdrawalInputMode: params.get('wMode') || 'rate', // 'rate' 또는 'amount'
            withdrawalRate: wRate,
            withdrawalAmount: wAmount,
            inflationRate: params.get('infRate') || "2.5",
            minSurvivalYears: Number(params.get('minSurv')) || 20, 

            isReady: !!(urlTickers && urlWeights)
        };
    };

    const initState = getInitialState();

    // --- 2. State 정의 ---
    const [rows, setRows] = useState(initState.rows);
    const [inputs, setInputs] = useState({
        totalInvestment: initState.investment,
        targetAmount: initState.targetAmount,
        currency: initState.currency,
        simYears: initState.simYears,
        simCount: initState.simCount,
        dataPeriod: initState.dataPeriod,
        startDate: initState.startDate,
        endDate: initState.endDate,
        isCustomDate: initState.isCustomDate,
        monthlyContribution: initState.monthlyContribution,
        contributionIncrease: initState.contributionIncrease,
        
        isWithdrawalMode: initState.isWithdrawalMode,
        withdrawalInputMode: initState.withdrawalInputMode,
        withdrawalRate: initState.withdrawalRate,
        withdrawalAmount: initState.withdrawalAmount,
        inflationRate: initState.inflationRate,
        minSurvivalYears: initState.minSurvivalYears
    });

    const [isLoading, setIsLoading] = useState(false);
    const [simulationResults, setSimulationResults] = useState(null); 
    const [milestoneData, setMilestoneData] = useState(null); 
    
    const [metaInfo, setMetaInfo] = useState({
        commonPeriod: { start: 'N/A', end: 'N/A', count: 0 },
        dataPeriodInfo: { requestedStartDate: 'N/A', limitingSymbolsInfo: [] }
    });

    const hasAutoRun = useRef(false);

    // --- 3. Helper Functions ---
    // 핵심 로직: 상호 연동 계산 및 총 투자금 변경 방어 로직 추가
   const updateInput = (field, value) => {
        setInputs(prev => {
            const newState = { ...prev, [field]: value };
            const totalInv = Number(newState.totalInvestment) || 0;

            if (totalInv > 0) {
                // 1. 인출율(%) 수정 시 -> 월 인출액 자동 계산
                if (field === 'withdrawalRate') {
                    const annualRate = parseFloat(value) || 0;
                    const annualAmount = totalInv * (annualRate / 100);
                    newState.withdrawalAmount = (annualAmount / 12).toFixed(0); // 월 단위로 저장
                } 
                
                // 2. 월 인출액 수정 시 -> 인출율(%) 자동 계산
                else if (field === 'withdrawalAmount') {
                    const monthlyAmount = parseFloat(value) || 0;
                    const annualAmount = monthlyAmount * 12;
                    newState.withdrawalRate = ((annualAmount / totalInv) * 100).toFixed(2);
                } 
                
                // 3. 총 투자금 수정 시 -> 현재 모드에 따라 반대쪽 값 갱신
                else if (field === 'totalInvestment' && newState.isWithdrawalMode) {
                    if (newState.withdrawalInputMode === 'amount') {
                        // 금액 고정 시: 투자금이 변하면 인출율(%)이 변함
                        const monthlyAmount = parseFloat(newState.withdrawalAmount) || 0;
                        newState.withdrawalRate = (((monthlyAmount * 12) / totalInv) * 100).toFixed(2);
                    } else {
                        // 비율 고정 시: 투자금이 변하면 월 인출액이 변함
                        const annualRate = parseFloat(newState.withdrawalRate) || 0;
                        newState.withdrawalAmount = ((totalInv * (annualRate / 100)) / 12).toFixed(0);
                    }
                }
            }
            return newState;
        });
    };

    const calculateTotalWeight = () => rows.reduce((acc, r) => acc + Number(r.weight || 0), 0);

    const calculateMilestoneData = (results, yearFilter = null) => {
        if (!results || !results.allPathsAtYear) return null;

        const allYears = Object.keys(results.allPathsAtYear).map(Number).sort((a, b) => a - b);
        const yearsToShow = yearFilter ? allYears.filter(year => year % 5 === 0) : allYears;
        const targetVal = Number(inputs.targetAmount); 

        return yearsToShow.map(year => {
            const values = results.allPathsAtYear[year] || [];
            const sorted = [...values].sort((a, b) => a - b);
            const p10Index = Math.floor(sorted.length * 0.10);
            const p50Index = Math.floor(sorted.length * 0.50);
            const p90Index = Math.floor(sorted.length * 0.90);

            const survivalCount = values.filter(v => v > 0).length;
            const survivalRate = (survivalCount / values.length) * 100;

            const goalReachCount = targetVal > 0 ? values.filter(v => v >= targetVal).length : 0;
            const goalReachRate = targetVal > 0 ? (goalReachCount / values.length) * 100 : 0;

            let principal = Number(inputs.totalInvestment);
            if (!inputs.isWithdrawalMode && Number(inputs.monthlyContribution) > 0) {
                let monthlyContrib = Number(inputs.monthlyContribution);
                for (let y = 0; y < year; y++) {
                    principal += monthlyContrib * 12;
                    if (Number(inputs.contributionIncrease) > 0) {
                        monthlyContrib *= (1 + Number(inputs.contributionIncrease) / 100);
                    }
                }
            }

            return {
                year,
                p10: sorted[p10Index],
                p50: sorted[p50Index],
                p90: sorted[p90Index],
                principal: Math.round(principal),
                survivalRate: survivalRate, 
                goalReachRate: goalReachRate 
            };
        });
    };

    const loadPortfolioIntoSimulation = (portfolioData) => {
        if (!portfolioData || !portfolioData.holdings) return;
        const newRows = portfolioData.holdings.map(h => ({
            ticker: h.symbol || h.ticker, 
            weight: (Number(h.weight) * 100).toFixed(2)
        }));
        setRows(newRows);
        toast.success(t('portfolio.loaded') || 'Portfolio Loaded Successfully!');
    };

    // --- 4. Simulation Execution ---
    const runSimulation = async (skipUrlUpdate = false, overrideRows = null) => {
        setIsLoading(true);
        setSimulationResults(null);

        const targetRows = overrideRows || rows;
        const validRows = targetRows.filter(r => r.ticker && Number(r.weight) > 0).map(r => ({ ...r, ticker: DOMPurify.sanitize(r.ticker.trim()) }));

        if (validRows.length === 0) {
            if (!overrideRows) toast.error(t('risk.alerts.noTickersOrWeights'));
            setIsLoading(false);
            return;
        }

        const currentWeightSum = targetRows.reduce((acc, r) => acc + Number(r.weight || 0), 0);
        
        if (!skipUrlUpdate && Math.abs(currentWeightSum - 100) > 0.01) {
            toast.error(t('risk.alerts.totalWeightNot100', { sum: currentWeightSum.toFixed(2) }));
            setIsLoading(false);
            return;
        }

        const investmentVal = Number(inputs.totalInvestment);
        const targetVal = Number(inputs.targetAmount);
        const monthlyVal = Number(inputs.monthlyContribution) || 0;
        const increaseVal = Number(inputs.contributionIncrease) || 0;
        const wRateVal = Number(inputs.withdrawalRate) || 4;
        const infRateVal = Number(inputs.inflationRate) || 2.5;

        if (!skipUrlUpdate) {
            const params = new URLSearchParams();
            params.set('tickers', validRows.map(r => r.ticker).join(','));
            params.set('weights', validRows.map(r => (Number(r.weight)/100).toFixed(4)).join(','));
            params.set('investment', investmentVal);
            if(targetVal > 0) params.set('targetAmount', targetVal);
            params.set('simYears', inputs.simYears);
            params.set('simCount', inputs.simCount);
            params.set('currency', inputs.currency);
            
            if (inputs.isWithdrawalMode) {
                params.set('mode', 'withdrawal');
                params.set('wRate', wRateVal);
                params.set('wAmount', inputs.withdrawalAmount); // URL에 Amount 추가
                params.set('wMode', inputs.withdrawalInputMode); // URL에 Mode 추가
                params.set('infRate', infRateVal);
                params.set('minSurv', inputs.minSurvivalYears);
            } else {
                if (monthlyVal > 0) params.set('monthly', monthlyVal);
                if (increaseVal > 0) params.set('increase', increaseVal);
            }

            if (inputs.isCustomDate) {
                params.set('startDate', inputs.startDate);
                params.set('endDate', inputs.endDate);
            } else {
                params.set('dataPeriod', inputs.dataPeriod);
            }
            navigate(`?${params.toString()}`, { replace: true });
        }

        try {
            const stockStr = validRows.map(r => encodeURIComponent(r.ticker)).join(',');
            const weightStr = validRows.map(r => (Number(r.weight)/100).toString()).join(',');
            const periodQuery = inputs.isCustomDate ? `&startDate=${inputs.startDate}&endDate=${inputs.endDate}` : `&dataPeriod=${inputs.dataPeriod}`;

            const res = await fetch(`/.netlify/functions/mc_simulation?stocks=${stockStr}&weights=${weightStr}${periodQuery}`);
            if (!res.ok) throw new Error(t('risk.dataError'));
            
            const stats = await res.json();
            if (!stats.returnsBlocks) throw new Error("No return data");

            setMetaInfo({
                commonPeriod: stats.commonPeriod,
                dataPeriodInfo: stats.dataPeriodInfo || {}
            });

            const historicalReturns = stats.returnsBlocks.map(dayRet => 
                dayRet.reduce((acc, val, idx) => acc + val * stats.weights[idx], 0)
            );

            // 주의: 워커에서는 기존대로 wRateVal을 사용한다고 가정했습니다. (기존 백엔드 호환성 유지)
            const workerResults = await runSimulationInWorkers(
                stats, inputs.simYears, inputs.simCount, investmentVal, targetVal, monthlyVal, increaseVal,
                inputs.isWithdrawalMode, wRateVal, infRateVal, inputs.minSurvivalYears
            );

            setSimulationResults({ ...workerResults, historicalReturns, isWithdrawalMode: inputs.isWithdrawalMode });

            const calculatedMilestones = calculateMilestoneData({ ...workerResults, historicalReturns, isWithdrawalMode: inputs.isWithdrawalMode }, true); 
            setMilestoneData(calculatedMilestones);

        } catch (err) {
            console.error(err);
            toast.error(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (initState.isReady && !hasAutoRun.current) {
            hasAutoRun.current = true;
            runSimulation(true);
        }
    }, []);

    return {
        rows, setRows, inputs, updateInput, isLoading, simulationResults, milestoneData, metaInfo,
        runSimulation, calculateTotalWeight, loadPortfolioIntoSimulation, calculateMilestoneData
    };
}